import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Field,
  PageHeader,
  SelectField,
  TextField,
  toast,
} from '@eon/ui';
import { resolveApiError, useTranslation } from '@eon/i18n';
import { hooks } from '@eon/api-client';
import { ArrowLeft } from 'lucide-react';

export function DeviceNewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const create = hooks.useCreateDevice();
  const [form, setForm] = useState({
    serialNumber: '',
    displayName: '',
    description: '',
    type: '',
    model: '',
    ocppVersion: '1.6',
    vendor: '',
    chargePointModel: '',
    chargeBoxSerialNumber: '',
    firmwareVersion: '',
    meterType: '',
    meterSerialNumber: '',
  });

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    const serialNumber = form.serialNumber.trim();
    if (!serialNumber) {
      toast.error(t('device.create.serialRequired'));
      return;
    }
    try {
      const device = await create.mutateAsync({
        serialNumber,
        displayName: form.displayName.trim() || undefined,
        description: form.description.trim() || undefined,
        type: form.type.trim() || undefined,
        model: form.model.trim() || undefined,
        vendor: form.vendor.trim() || undefined,
        chargePointModel: form.chargePointModel.trim() || undefined,
        chargeBoxSerialNumber: form.chargeBoxSerialNumber.trim() || undefined,
        firmwareVersion: form.firmwareVersion.trim() || undefined,
        ocppVersion: form.ocppVersion,
        meterType: form.meterType.trim() || undefined,
        meterSerialNumber: form.meterSerialNumber.trim() || undefined,
        isActive: true,
        registered: false,
      });
      toast.success(t('device.create.success'));
      navigate({ to: '/devices/$deviceId', params: { deviceId: device.id } });
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  };

  return (
    <>
      <PageHeader
        title={t('device.create.title')}
        description={t('device.create.description')}
        actions={
          <Button variant="outline" asChild>
            <Link to="/devices">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('device.create.back')}
            </Link>
          </Button>
        }
      />

      <Card className="max-w-4xl">
        <CardHeader>
          <CardTitle className="text-base">{t('device.create.formTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label={t('device.serialNumber')} required>
              <TextField
                value={form.serialNumber}
                onChange={(e) => set('serialNumber', e.target.value)}
                placeholder={t('device.create.serialPlaceholder')}
                className="font-mono"
                autoFocus
              />
            </Field>
            <Field label={t('device.create.displayName')}>
              <TextField
                value={form.displayName}
                onChange={(e) => set('displayName', e.target.value)}
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label={t('device.create.descriptionField')}>
                <TextField
                  value={form.description}
                  onChange={(e) => set('description', e.target.value)}
                />
              </Field>
            </div>
            <Field label={t('device.create.type')}>
              <TextField value={form.type} onChange={(e) => set('type', e.target.value)} />
            </Field>
            <Field label={t('device.model')}>
              <TextField value={form.model} onChange={(e) => set('model', e.target.value)} />
            </Field>
            <Field label={t('device.create.protocol')}>
              <SelectField
                value={form.ocppVersion}
                onValueChange={(v) => set('ocppVersion', v)}
                options={[
                  { value: '1.6', label: 'OCPP 1.6' },
                  { value: '2.0.1', label: 'OCPP 2.0.1' },
                ]}
              />
            </Field>
            <Field label={t('device.create.vendor')}>
              <TextField value={form.vendor} onChange={(e) => set('vendor', e.target.value)} />
            </Field>
            <Field label={t('device.create.chargePointModel')}>
              <TextField
                value={form.chargePointModel}
                onChange={(e) => set('chargePointModel', e.target.value)}
              />
            </Field>
            <Field label={t('device.create.chargeBoxSerial')}>
              <TextField
                value={form.chargeBoxSerialNumber}
                onChange={(e) => set('chargeBoxSerialNumber', e.target.value)}
                className="font-mono text-sm"
              />
            </Field>
            <Field label={t('device.firmware')}>
              <TextField
                value={form.firmwareVersion}
                onChange={(e) => set('firmwareVersion', e.target.value)}
                className="font-mono text-sm"
              />
            </Field>
            <Field label={t('device.create.meterType')}>
              <TextField value={form.meterType} onChange={(e) => set('meterType', e.target.value)} />
            </Field>
            <Field label={t('device.create.meterSerial')}>
              <TextField
                value={form.meterSerialNumber}
                onChange={(e) => set('meterSerialNumber', e.target.value)}
                className="font-mono text-sm"
              />
            </Field>
          </div>
        </CardContent>
        <CardFooter className="justify-end gap-2 border-t pt-4">
          <Button variant="outline" asChild disabled={create.isPending}>
            <Link to="/devices">{t('actions.cancel')}</Link>
          </Button>
          <Button onClick={submit} disabled={create.isPending || !form.serialNumber.trim()}>
            {create.isPending ? t('device.create.saving') : t('device.create.submit')}
          </Button>
        </CardFooter>
      </Card>
    </>
  );
}
